import Component from '@glimmer/component';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import gte from 'ember-truth-helpers/helpers/gte';
import type { Signature as ForcProviderBaseSignature } from 'parog-web/components/forc/provider/base';
import type Patient from 'parog-web/models/patient';
import type MedicalHistoryFormComponent from '../../diabeto';

interface Signature {
  Args: {
    forcComponent?: MedicalHistoryFormComponent;
    form?: ForcProviderBaseSignature['Blocks']['default'][0];
    patient: Patient;
    isMandatory?: boolean;
  };
}

export default class extends Component<Signature> {
  get isMandatory(): boolean {
    return this.args.isMandatory ?? true;
  }

  <template>
    <div class='two fields'>
      <div class='field'>
        <@form.Date
          @labelName='Date de naissance'
          @propertyName='birthday'
          @isMandatory={{this.isMandatory}}
          @onChange={{@forcComponent.updateAge}}
        />
      </div>
      <div class='field'>
        <@form.NumberInput
          @labelName='Âge'
          @propertyName='age'
          @isReadOnly={{true}}
        />
      </div>
    </div>
    {{#if (gte @forcComponent.age 75)}}
      <div class='field'>
        <label>
          Santé du patient{{if this.isMandatory ' *'}}
        </label>
        <div class='three fields inline'>
          <@form.BasicRadioGroup @propertyName='state' as |Group|>
            <Group.FieldRadio @label='Bonne santé' @value='1' />
            <Group.FieldRadio @label='Fragile' @value='2' />
            <Group.FieldRadio @label='Dépendant' @value='3' />
          </@form.BasicRadioGroup>
        </div>
      </div>
    {{/if}}

    <div class='two fields'>
      <div class='field'>
        <@form.NumberInput
          @labelName='Poids (en kg)'
          @propertyName='weight'
          @placeholder='Ex: 80'
          @isMandatory={{this.isMandatory}}
        />
      </div>
      <div class='field'>
        <@form.NumberInput
          @labelName='Taille (en cm)'
          @propertyName='height'
          @placeholder='Ex: 180'
          @isMandatory={{this.isMandatory}}
        />
      </div>
    </div>

    <div class='two fields'>
      <div class='field'>
        <@form.NumberInput
          @labelName='Année du diagnostic du diabète'
          @propertyName='year'
          @isMandatory={{this.isMandatory}}
        />
      </div>
      <div class='field'>
        <label>
          Pathologie évolutive ou menaçant le pronostic vital à court termes{{if
            this.isMandatory
            ' *'
          }}
        </label>
        <div class='two fields inline'>
          {{! Keys are inverted because the label doesn''t really fit with the real question for diabguide I.E : "espérance de vie > 5 ans ?" }}
          <@form.BasicRadioGroup @propertyName='exp' as |Group|>
            <Group.FieldRadio @label='Oui' @value={{false}} />
            <Group.FieldRadio @label='Non' @value={{true}} />
          </@form.BasicRadioGroup>
        </div>
      </div>
    </div>

    <div class='two fields'>
      <div class='field'>
        <@form.NumberInput
          @labelName='Dernière HBA1C (%)'
          @placeholder='8,5'
          @propertyName='hba'
          @isMandatory={{this.isMandatory}}
        />
      </div>
      <div class='field'>
        <@form.Date
          @labelName='Date de la dernière HBA1C'
          @propertyName='hbaDate'
          @isMandatory={{this.isMandatory}}
        />
      </div>
    </div>

    <div class='field'>
      <label>
        Fumeur ?{{if this.isMandatory ' *'}}
      </label>
      <div class='fields inline'>
        <@form.BasicRadioGroup @propertyName='smoker' as |Group|>
          <Group.FieldRadio @label='Oui' @value={{true}} />
          <Group.FieldRadio @label='Non' @value={{false}} />
        </@form.BasicRadioGroup>
      </div>
    </div>
    <div class='field'>
      <label>
        Activité physique{{if this.isMandatory ' *'}}
      </label>
      <div class='fields inline'>
        <@form.BasicRadioGroup @propertyName='doSport' as |Group|>
          <Group.FieldRadio @label='Oui' @value='true' />
          <Group.FieldRadio @label='Non' @value='false' />
          <Group.FieldRadio @label='A arrêté' @value='stoped' />
        </@form.BasicRadioGroup>
      </div>
    </div>
    <div class='field'>
      <label>
        Votre patient a vu un diabétologue dans les deux dernières années pour
        le suivi de son diabète de type 2
      </label>
      <div class='fields inline'>
        <@form.BasicRadioGroup
          @propertyName='isFollowingDiabetologist'
          as |Group|
        >
          <Group.FieldRadio @label='Oui' @value={{true}} />
          <Group.FieldRadio @label='Non' @value={{false}} />
        </@form.BasicRadioGroup>
      </div>
      {{#if (changesetGet @forcComponent.changeset 'isFollowingDiabetologist')}}
        <label data-test-followed-by-diabetologist class='!text-[#ff695e]'>
          Si votre patient a vu un diabétologue dans les deux dernières années
          pour le suivi de son diabète de type 2, il n’est pas éligible dans le
          cadre de cette étude
        </label>
      {{/if}}
    </div>
  </template>
}
